import React, { useEffect, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UtilityService from '../../../services/UtilityService';
import { CustomFieldContract } from '../../../types/CustomField';
import CustomFieldInput from '../CustomFieldInput';
import { getCustomFieldsSchema } from '../../../models/schemas/CustomFields';

export const CustomFieldListTestId = 'CustomFieldList-input';

export type CustomFieldListData = {
  id: number;
  value: string;
};

export type CustomFieldListProps = {
  customFields: CustomFieldContract[];
  onValidData: (customFieldListData: CustomFieldListData) => void;
  onErrorStatus?: (hasError: boolean) => void;
  useContext?: boolean;
};

const extractFieldData = (customFields: CustomFieldContract[]) => {
  const { fieldNames, fieldValues } = customFields.reduce(
    (
      acc: { fieldNames: string[]; fieldValues: Array<string | undefined> },
      field
    ) => {
      acc.fieldNames.push(UtilityService.buildCustomFieldInputName(field));
      const value = field.value !== '' ? field.value : undefined;
      acc.fieldValues.push(value);
      return acc;
    },
    { fieldNames: [], fieldValues: [] }
  );

  const defaultValues = fieldNames.reduce(
    (acc: { [key: string]: string | undefined }, fieldName, index) => {
      acc[fieldName] = fieldValues[index] ?? undefined;
      return acc;
    },
    {}
  );
  return { fieldNames, defaultValues };
};

const CustomFieldList: React.FC<CustomFieldListProps> = ({
  customFields,
  onValidData,
  onErrorStatus,
  useContext,
}: CustomFieldListProps) => {
  const [hasFormError, setHasFormError] = useState(false);
  const customFieldsSchema = getCustomFieldsSchema(customFields);
  const { fieldNames, defaultValues } = extractFieldData(customFields);
  const formContext = useFormContext<{
    customFields: {
      [key: string]: unknown;
    };
  }>();
  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(customFieldsSchema),
    defaultValues,
  });

  const watchAll = useContext ? null : form.watch(fieldNames, defaultValues);

  const handleInputOnChange = (
    id: number,
    fieldName: string,
    value: string
  ): void => {
    onValidData({ id, value });
  };

  const handleValidateOnBlur = (
    event: React.FocusEvent<HTMLInputElement>
  ): void => {
    if (!useContext) {
      form.trigger(event.target.name);
    }
  };

  const handleStringInputOnChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: CustomFieldContract,
    fieldName: string
  ): Promise<void> => {
    if (!useContext) {
      event.persist();
      await form.trigger(fieldName);
      const hasError = !!form.formState.errors[fieldName]?.message;
      if (!hasError)
        handleInputOnChange(field.id, fieldName, event.target.value);
    }
  };

  const handleBooleanInputOnClick = async (
    event: React.ChangeEvent<HTMLInputElement>,
    field: CustomFieldContract,
    fieldName: string
  ): Promise<void> => {
    const value = event.target.checked ? 'Y' : 'N';
    handleInputOnChange(field.id, fieldName, value);
  };

  useEffect(() => {
    const newErrorState = !!Object.keys(form.formState.errors).length;
    if (newErrorState !== hasFormError) {
      setHasFormError(newErrorState);
      onErrorStatus?.(newErrorState);
    }
  }, [
    form.formState.errors,
    watchAll,
    hasFormError,
    setHasFormError,
    onErrorStatus,
  ]);

  return (
    <div data-testid={CustomFieldListTestId}>
      {customFields.map(field => {
        const fieldName = useContext
          ? UtilityService.buildCustomFieldInputNameWithFormContext(field)
          : UtilityService.buildCustomFieldInputName(field);
        const customFieldType = field.inputType.toUpperCase();

        let onChange;
        if (customFieldType === 'CHECKBOX') {
          onChange = (e: React.ChangeEvent<HTMLInputElement>): Promise<void> =>
            handleBooleanInputOnClick(e, field, fieldName);
        } else {
          onChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
          ): Promise<void> => handleStringInputOnChange(e, field, fieldName);
        }

        return (
          <CustomFieldInput
            customType={customFieldType}
            key={field.id}
            customField={field}
            errorLabel={
              useContext
                ? formContext.formState.errors?.customFields?.[fieldName]
                    ?.message
                : form.formState.errors[fieldName]?.message
            }
            register={useContext ? formContext.register : form.register}
            onChange={onChange}
            onBlur={handleValidateOnBlur}
            name={useContext ? `customFields.${fieldName}` : fieldName}
            clearErrors={useContext ? formContext.clearErrors : undefined}
          />
        );
      })}
    </div>
  );
};

export default CustomFieldList;
